import React, { useState } from 'react';
import { MessageCircle, Users, Calendar, TrendingUp, Plus, Search, Video, FileText, CheckCircle } from 'lucide-react';
import NewCoachingSessionModal from '../components/NewCoachingSessionModal';

interface CoachingSession {
  id: string;
  clientName: string;
  sessionType: 'individual' | 'group' | 'online';
  topic: string;
  date: string;
  time: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes?: string;
}

const DatingCoach = () => {
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isNewSessionModalOpen, setIsNewSessionModalOpen] = useState(false);

  const handleNewSession = (sessionData: Omit<CoachingSession, 'id'>) => {
    const newSession: CoachingSession = {
      ...sessionData,
      id: Date.now().toString(),
    };
    setSessions([...sessions, newSession]);
  };

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         session.topic.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || session.sessionType === filterType;
    const matchesStatus = filterStatus === 'all' || session.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getSessionTypeLabel = (type: string) => {
    switch (type) {
      case 'individual':
        return 'Individuale';
      case 'group':
        return 'Gruppo';
      case 'online':
        return 'Online';
      default:
        return type;
    }
  };

  const getSessionTypeColor = (type: string) => {
    switch (type) {
      case 'individual':
        return 'bg-blue-100 text-blue-800';
      case 'group':
        return 'bg-purple-100 text-purple-800';
      case 'online':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'Programmata';
      case 'completed':
        return 'Completata';
      case 'cancelled':
        return 'Annullata';
      default:
        return status;
    }
  };

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white">Dating Coach</h1>
            <p className="text-white/80 mt-1">Gestisci le sessioni di coaching e i percorsi di crescita personale</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Sessioni Totali</p>
                  <p className="text-3xl font-bold text-white mt-1">{sessions.length}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Programmate</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {sessions.filter(s => s.status === 'scheduled').length}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Completate</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {sessions.filter(s => s.status === 'completed').length}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Clienti Attivi</p>
                  <p className="text-3xl font-bold text-white mt-1">0</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca per cliente o argomento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tutti i Tipi</option>
              <option value="individual">Individuale</option>
              <option value="group">Gruppo</option>
              <option value="online">Online</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tutti gli Stati</option>
              <option value="scheduled">Programmate</option>
              <option value="completed">Completate</option>
              <option value="cancelled">Annullate</option>
            </select>
            <button
              onClick={() => setIsNewSessionModalOpen(true)}
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors flex items-center gap-2 font-medium shadow-sm whitespace-nowrap"
            >
              <Plus className="h-5 w-5" />
              Nuova Sessione
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {filteredSessions.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessuna Sessione Trovata
              </h3>
              <p className="text-gray-600 mb-6">
                {sessions.length === 0
                  ? "Inizia programmando la tua prima sessione di coaching"
                  : "Nessuna sessione corrisponde ai criteri di ricerca"}
              </p>
              {sessions.length === 0 && (
                <button
                  onClick={() => setIsNewSessionModalOpen(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Programma Prima Sessione
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Cliente</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Argomento</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data e Ora</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Durata</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Stato</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{session.clientName}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSessionTypeColor(session.sessionType)}`}>
                        {getSessionTypeLabel(session.sessionType)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {session.topic}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(session.date).toLocaleDateString('it-IT')} - {session.time}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {session.duration} min
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {getStatusLabel(session.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        {session.sessionType === 'online' && (
                          <button
                            className="text-green-600 hover:text-green-700 p-1 hover:bg-green-50 rounded"
                            title="Avvia Video Call"
                          >
                            <Video className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                          title="Note"
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <NewCoachingSessionModal
        isOpen={isNewSessionModalOpen}
        onClose={() => setIsNewSessionModalOpen(false)}
        onSubmit={handleNewSession}
      />
    </div>
  );
};

export default DatingCoach;
